// Admin · Overview. This file and server/admin_overview.go are the whole section.
//
// THE PAGE YOU OPEN WHEN SOMETHING IS ALREADY WRONG. It answers one question —
// is production what I think it is, and is its data current? — and it exists
// because of one incident: the SVP depth cap was lifted in code on 2026-08-14,
// production went on serving 3,000 m profiles for weeks because svp-fetch was
// still running a 2026-07-27 image, and no screen anywhere said so.
//
// So: Attention leads, because "is everything fine" must be answerable in one
// glance and not by reading four cards. Every age is printed as words AND as an
// absolute UTC instant, because "2 days ago" on a page left open overnight is a
// different claim from a date. Nothing is ever blank: an empty Attention list
// says it is empty, and a fetch that failed says what failed.

// --- payload ----------------------------------------------------------------

type State = "ok" | "warn" | "bad";

interface Stamp {
  at: string;
  ageSec: number;
  state: State;
  known: boolean;
}

interface BuildInfo {
  build: string;
  builtAt: string;
  state: State;
  note: string;
}

interface SvpInfo {
  error: string;
  resident: boolean;
  written: Stamp;
  forecastDate: string;
  source: string;
  cells: number;
  stepDeg: number;
  generated: string;
  deepestM: number;
  levels: number;
  depthState: State;
  depthNote: string;
}

interface FieldInfo {
  error: string;
  resident: boolean;
  written: Stamp;
  cycle: string;
  cycleAge: Stamp;
  source: string;
  hours: number;
  regions: number;
  generated: string;
}

interface UpstreamInfo {
  omTier: string;
  omKeyed: boolean;
  omHost: string;
  omState: State;
  omNote: string;
  db: string;
  dbState: State;
  dbNote: string;
}

interface Overview {
  now: string;
  build: BuildInfo;
  svp: SvpInfo;
  field: FieldInfo;
  upstream: UpstreamInfo;
  warnHours: number;
  badHours: number;
}

// --- narrowing --------------------------------------------------------------

function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function str(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function num(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}

/** A state we do not recognise is treated as "bad": an unreadable verdict is a
 *  problem, and quietly rendering it as green would be the one failure mode this
 *  whole page is built to prevent. */
function state(value: unknown): State {
  return value === "ok" || value === "warn" || value === "bad" ? value : "bad";
}

function stamp(value: unknown): Stamp {
  const r = asRecord(value);
  if (r === null) return { at: "", ageSec: 0, state: "bad", known: false };
  return {
    at: str(r.at),
    ageSec: num(r.ageSec),
    state: state(r.state),
    known: r.known === true,
  };
}

function narrowOverview(raw: unknown): Overview | null {
  const r = asRecord(raw);
  if (r === null) return null;
  const b = asRecord(r.build);
  const s = asRecord(r.svp);
  const f = asRecord(r.field);
  const u = asRecord(r.upstream);
  const t = asRecord(r.thresholds);
  if (b === null || s === null || f === null || u === null) return null;
  return {
    now: str(r.now),
    build: {
      build: str(b.build),
      builtAt: str(b.builtAt),
      state: state(b.state),
      note: str(b.note),
    },
    svp: {
      error: str(s.error),
      resident: s.resident === true,
      written: stamp(s.written),
      forecastDate: str(s.forecastDate),
      source: str(s.source),
      cells: num(s.cells),
      stepDeg: num(s.stepDeg),
      generated: str(s.generated),
      deepestM: num(s.deepestM),
      levels: num(s.levels),
      depthState: state(s.depthState),
      depthNote: str(s.depthNote),
    },
    field: {
      error: str(f.error),
      resident: f.resident === true,
      written: stamp(f.written),
      cycle: str(f.cycle),
      cycleAge: stamp(f.cycleAge),
      source: str(f.source),
      hours: num(f.hours),
      regions: num(f.regions),
      generated: str(f.generated),
    },
    upstream: {
      omTier: str(u.omTier),
      omKeyed: u.omKeyed === true,
      omHost: str(u.omHost),
      omState: state(u.omState),
      omNote: str(u.omNote),
      db: str(u.db),
      dbState: state(u.dbState),
      dbNote: str(u.dbNote),
    },
    warnHours: t === null ? 0 : num(t.warnHours),
    badHours: t === null ? 0 : num(t.badHours),
  };
}

// --- formatting -------------------------------------------------------------

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== undefined) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** An instant, always in UTC and always spelled out. The console has a local/UTC
 *  preference; this page does not — two people reading it are comparing against
 *  cron schedules and model cycles, which are UTC and nothing else. */
function fmtUtc(iso: string): string {
  if (iso === "") return "unknown";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const mon = MONTHS[d.getUTCMonth()] ?? "?";
  return (
    `${pad(d.getUTCDate())} ${mon} ${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())} UTC`
  );
}

function plural(n: number, unit: string): string {
  return `${n} ${unit}${n === 1 ? "" : "s"} ago`;
}

/** Age in the words a person would use. Never shown alone — always beside the
 *  absolute instant it was computed from. */
function relAge(seconds: number): string {
  if (!Number.isFinite(seconds)) return "age unknown";
  // A negative age means the server's clock and the stamp disagree. Say that
  // rather than rendering "0 minutes ago" over a timestamp in the future.
  if (seconds < -90) return "timestamped in the future";
  const s = Math.max(0, Math.round(seconds));
  if (s < 90) return "just now";
  const m = Math.round(s / 60);
  if (m < 90) return plural(m, "minute");
  // Hours all the way to two days: the thresholds this page grades against are
  // 30 h and 72 h, and "2 days ago" is too blunt to sit next to either.
  const h = Math.round(s / 3600);
  if (h < 48) return plural(h, "hour");
  return plural(Math.round(s / 86400), "day");
}

function fmtCount(n: number): string {
  return n.toLocaleString("en-US");
}

/** Metres, to one decimal, grouped. The deepest level is the number the incident
 *  turned on, so it is printed exactly rather than rounded to a tidy band. */
function fmtMetres(m: number): string {
  return `${m.toLocaleString("en-US", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} m`;
}

// --- pieces -----------------------------------------------------------------

function card(title: string): { root: HTMLElement; body: HTMLElement } {
  const root = el("section", "ad-card");
  root.appendChild(el("h2", undefined, title));
  const body = el("div", "ad-body");
  root.appendChild(body);
  return { root, body };
}

function stateTag(s: State, word: string): HTMLElement {
  return el("span", `ad-state ${s}`, word);
}

/** One key/value row. `value` may be a string (plain text) or a built node. */
function kv(dl: HTMLElement, key: string, value: string | Node, mono = false): void {
  dl.appendChild(el("dt", undefined, key));
  const dd = el("dd", mono ? "mono" : undefined);
  if (typeof value === "string") dd.textContent = value;
  else dd.appendChild(value);
  dl.appendChild(dd);
}

/** An age, said twice: the absolute UTC instant, then the words. */
function whenCell(s: Stamp): HTMLElement {
  const dd = el("span");
  if (!s.known || s.at === "") {
    dd.appendChild(el("span", undefined, "unknown"));
    return dd;
  }
  dd.appendChild(el("span", "mono", fmtUtc(s.at)));
  dd.appendChild(document.createTextNode(" "));
  dd.appendChild(el("span", "ad-when", `· ${relAge(s.ageSec)}`));
  return dd;
}

const FRESH_WORD: Record<State, string> = {
  ok: "Current",
  warn: "Stale",
  bad: "Badly stale",
};

/** The worse of two verdicts. */
function worse(a: State, b: State): State {
  if (a === "bad" || b === "bad") return "bad";
  if (a === "warn" || b === "warn") return "warn";
  return "ok";
}

// --- the four cards ---------------------------------------------------------

function buildCard(d: Overview): HTMLElement {
  const { root, body } = card("Build");
  const dl = el("dl", "ad-kv");

  kv(dl, "Commit", d.build.build === "" ? "unknown" : d.build.build, true);

  if (d.build.builtAt === "") {
    kv(dl, "Built", "not stamped");
  } else {
    const when = el("span");
    when.appendChild(el("span", "mono", fmtUtc(d.build.builtAt)));
    when.appendChild(document.createTextNode(" "));
    const age = (new Date(d.now).getTime() - new Date(d.build.builtAt).getTime()) / 1000;
    if (Number.isFinite(age)) when.appendChild(el("span", "ad-when", `· ${relAge(age)}`));
    kv(dl, "Built", when);
  }

  kv(
    dl,
    "Stamp",
    stateTag(d.build.state, d.build.state === "ok" ? "Stamped by the deploy" : "Not a deploy stamp"),
  );
  if (d.build.note !== "") kv(dl, "Meaning", d.build.note);

  body.appendChild(dl);
  return root;
}

function svpBlock(d: Overview): HTMLElement {
  const box = el("div");
  const dl = el("dl", "ad-kv");
  const s = d.svp;

  kv(dl, "Grid", "Sound velocity (SVP)");
  kv(dl, "Freshness", stateTag(s.written.state, FRESH_WORD[s.written.state]));
  kv(dl, "Written", whenCell(s.written));
  if (s.forecastDate !== "") kv(dl, "Forecast day", s.forecastDate, true);
  if (s.generated !== "") kv(dl, "Generated", fmtUtc(s.generated), true);
  if (s.source !== "") kv(dl, "Source", s.source);

  const size = s.stepDeg > 0
    ? `${fmtCount(s.cells)} cells at ${Number(s.stepDeg.toFixed(3))}°`
    : `${fmtCount(s.cells)} cells`;
  kv(dl, "Coverage", s.resident ? size : `${size} (not resident)`);

  // The number the incident turned on. Its own state, because a grid can be
  // written on time every day and still be silently capped.
  const deep = el("span");
  deep.appendChild(
    stateTag(s.depthState, s.levels > 0 ? fmtMetres(s.deepestM) : "no levels"),
  );
  kv(dl, "Deepest level", deep);
  kv(dl, "Levels", s.levels > 0 ? `${fmtCount(s.levels)} in the deepest column` : "none");

  if (s.depthNote !== "") kv(dl, "Depth note", s.depthNote);
  if (s.error !== "") kv(dl, "Read error", s.error);

  box.appendChild(dl);
  return box;
}

function fieldBlock(d: Overview): HTMLElement {
  const box = el("div");
  const dl = el("dl", "ad-kv");
  const f = d.field;

  kv(dl, "Grid", "Wind & wave field");
  kv(dl, "Freshness", stateTag(f.written.state, FRESH_WORD[f.written.state]));
  kv(dl, "Written", whenCell(f.written));

  if (f.cycle !== "") {
    const cyc = el("span");
    cyc.appendChild(el("span", "mono", f.cycle));
    if (f.cycleAge.known) {
      cyc.appendChild(document.createTextNode(" "));
      cyc.appendChild(
        el("span", "ad-when", `· ${fmtUtc(f.cycleAge.at)} · ${relAge(f.cycleAge.ageSec)}`),
      );
    }
    kv(dl, "Model cycle", cyc);
    // Two ages, not one: a fetch cron that keeps running against a stuck
    // upstream writes a fresh row full of stale weather, and only this shows it.
    kv(dl, "Cycle age", stateTag(f.cycleAge.state, FRESH_WORD[f.cycleAge.state]));
  } else {
    kv(dl, "Model cycle", "unknown");
  }

  if (f.generated !== "") kv(dl, "Generated", fmtUtc(f.generated), true);
  if (f.source !== "") kv(dl, "Source", f.source);
  kv(
    dl,
    "Coverage",
    `${fmtCount(f.regions)} region${f.regions === 1 ? "" : "s"}, ${fmtCount(f.hours)} forecast hours`,
  );
  if (f.error !== "") kv(dl, "Read error", f.error);

  box.appendChild(dl);
  return box;
}

function freshnessCard(d: Overview): HTMLElement {
  const { root, body } = card("Data freshness");
  const grid = el("div", "ad-grid");
  grid.append(svpBlock(d), fieldBlock(d));
  body.appendChild(grid);
  root.appendChild(
    el(
      "p",
      "ad-empty",
      `Freshness is the age of the last write by the fetch cron: amber past ` +
        `${fmtCount(d.warnHours)} h, red past ${fmtCount(d.badHours)} h. ` +
        `Both grids are refreshed daily or better, so a day and a bit without a ` +
        `write is a failed cron rather than a slow one.`,
    ),
  );
  return root;
}

function upstreamCard(d: Overview): HTMLElement {
  const { root, body } = card("Upstream");
  const dl = el("dl", "ad-kv");
  const u = d.upstream;

  kv(
    dl,
    "Open-Meteo",
    stateTag(
      u.omState,
      u.omTier === "professional" ? "Professional tier" : "Free tier — no key set",
    ),
  );
  kv(dl, "Relaying to", u.omHost === "" ? "unknown" : u.omHost, true);
  if (u.omNote !== "") kv(dl, "Meaning", u.omNote);

  const dbWord = u.db === "ok" ? "Reachable" : u.db === "down" ? "Down" : "Not configured";
  kv(dl, "Database", stateTag(u.dbState, dbWord));
  if (u.dbNote !== "") kv(dl, "Meaning", u.dbNote);

  body.appendChild(dl);
  return root;
}

// --- attention --------------------------------------------------------------

interface Item {
  what: string;
  state: State;
  detail: string;
}

/** Everything currently not "ok", collected from the very same verdicts the
 *  cards below print — so this list can never disagree with them. */
function attentionItems(d: Overview): Item[] {
  const items: Item[] = [];
  const add = (what: string, s: State, detail: string): void => {
    if (s !== "ok") items.push({ what, state: s, detail });
  };

  add("Build stamp", d.build.state, d.build.note !== "" ? d.build.note : `Build reads "${d.build.build}".`);

  add(
    "SVP grid freshness",
    d.svp.written.state,
    d.svp.written.known
      ? `Last written ${relAge(d.svp.written.ageSec)} (${fmtUtc(d.svp.written.at)}).`
      : "No write time could be read for the SVP grid.",
  );
  add("SVP depth reach", d.svp.depthState, d.svp.depthNote);
  if (d.svp.error !== "") items.push({ what: "SVP grid row", state: "bad", detail: d.svp.error });

  add(
    "Field grid freshness",
    d.field.written.state,
    d.field.written.known
      ? `Last written ${relAge(d.field.written.ageSec)} (${fmtUtc(d.field.written.at)}).`
      : "No write time could be read for the field grid.",
  );
  add(
    "Field model cycle",
    d.field.cycleAge.state,
    d.field.cycleAge.known
      ? `Cycle ${d.field.cycle} (${fmtUtc(d.field.cycleAge.at)}) is ${relAge(d.field.cycleAge.ageSec)}.`
      : "The stored cycle could not be read as a time.",
  );
  if (d.field.error !== "") items.push({ what: "Field grid row", state: "bad", detail: d.field.error });

  add("Open-Meteo tier", d.upstream.omState, d.upstream.omNote);
  add(
    "Database",
    d.upstream.dbState,
    d.upstream.dbNote !== "" ? d.upstream.dbNote : `Database reports "${d.upstream.db}".`,
  );

  // Worst first: the reason the page was opened should be the first row.
  const rank: Record<State, number> = { bad: 0, warn: 1, ok: 2 };
  return items.sort((a, b) => rank[a.state] - rank[b.state]);
}

const ITEM_WORD: Record<State, string> = { ok: "OK", warn: "Warn", bad: "Bad" };

function attentionCard(d: Overview): HTMLElement {
  const items = attentionItems(d);
  const root = el("section", "ad-card");
  const bad = items.filter((i) => i.state === "bad").length;
  const warn = items.length - bad;
  root.appendChild(
    el(
      "h2",
      undefined,
      items.length === 0 ? "Attention — nothing" : `Attention — ${bad} bad, ${warn} warn`,
    ),
  );

  if (items.length === 0) {
    // Never ambiguous between "fine" and "failed to load": this line only ever
    // renders after a successful read, and says what it checked and when.
    root.appendChild(
      el(
        "p",
        "ad-empty",
        `Nothing needs attention. Every check on this page is green, as of ${fmtUtc(d.now)}.`,
      ),
    );
    return root;
  }

  const table = el("table", "ad-table");
  const thead = el("thead");
  const hr = el("tr");
  for (const h of ["What", "State", "Detail"]) hr.appendChild(el("th", undefined, h));
  thead.appendChild(hr);
  const tbody = el("tbody");
  for (const it of items) {
    const tr = el("tr");
    tr.appendChild(el("td", undefined, it.what));
    const st = el("td");
    st.appendChild(stateTag(it.state, ITEM_WORD[it.state]));
    tr.appendChild(st);
    tr.appendChild(el("td", undefined, it.detail));
    tbody.appendChild(tr);
  }
  table.append(thead, tbody);
  root.appendChild(table);
  return root;
}

// --- mount ------------------------------------------------------------------

/** A failure says what failed, not nothing. An admin who opens this page during
 *  an incident is owed the difference between "refused", "unreachable" and "the
 *  body was not what this page reads". */
function failureCard(what: string, detail: string): HTMLElement {
  const { root, body } = card("Overview unavailable");
  const dl = el("dl", "ad-kv");
  kv(dl, "Failed", what);
  kv(dl, "Detail", detail);
  kv(dl, "Endpoint", "GET /api/admin/overview", true);
  body.appendChild(dl);
  return root;
}

async function load(host: HTMLElement): Promise<void> {
  let res: Response;
  try {
    res = await fetch("/api/admin/overview", { credentials: "same-origin" });
  } catch {
    host.replaceChildren(
      failureCard("Could not reach the server", "The request never completed — network or the server is down."),
    );
    return;
  }
  if (!res.ok) {
    let detail = `The server answered HTTP ${res.status}.`;
    try {
      const rec = asRecord(await res.json());
      const err = rec === null ? "" : str(rec.error);
      if (err !== "") detail = `${detail} ${err}`;
    } catch {
      // Non-JSON error body — the status alone stands.
    }
    if (res.status === 404) {
      detail = `${detail} A non-admin account is answered with 404 by design (server/admin.go).`;
    }
    host.replaceChildren(failureCard("The overview request was refused", detail));
    return;
  }

  let raw: unknown;
  try {
    raw = await res.json();
  } catch {
    host.replaceChildren(failureCard("Malformed response", "The body was not JSON."));
    return;
  }
  const data = narrowOverview(raw);
  if (data === null) {
    host.replaceChildren(
      failureCard("Unexpected response shape", "The payload did not carry the fields this page reads."),
    );
    return;
  }

  host.replaceChildren(attentionCard(data), buildCard(data), freshnessCard(data), upstreamCard(data));
}

export function mountAdminOverview(host: HTMLElement): void {
  host.replaceChildren(el("p", "ad-empty", "Reading the deployment…"));
  void load(host);
}
